import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import DropDownMultiple from "@components/ui/dropdown/DropDownMultiple";
import Button from "@components/ui/buttons/Button";
import { useRef, useState } from "react";
import TextField from "@components/ui/textfields/TextField";
import { PartnerDecennale, PartnerInfos } from "types";
import { showNotification } from "context/notification.service";
import { useProjectStore } from "@store/projectStore";
import { partnersApi } from "@services/api/partners";
import { PDFViewerModal } from "@components/modals/PDFViewerModal";
import { AddDecennaleModal } from "@components/modals/AddDecennaleModal";
import { formatIsoDate } from "@services/timeHelpers";
import { useDataStore } from "@store/user_org";
import { isOwnerOrAdmin } from "@utils/permissions";

import { COMPANY_TYPE_OPTIONS, HIDDEN_COMPANY_TYPES } from "@constants/partnerTypes";

export const ModifyPartnerModal = ({
  onClose,
  initialPartner,
}: {
  onClose: () => void;
  initialPartner: PartnerInfos;
}) => {
  const updatePartner = useProjectStore((state) => state.updatePartner);
  const removePartners = useProjectStore((state) => state.removePartners);
  const userData = useDataStore((state) => state.userData);
  const canDelete = isOwnerOrAdmin(userData);
  const [partner, setPartner] = useState<PartnerInfos>({ ...initialPartner });
  const [showAddDecennale, setShowAddDecennale] = useState(false);
  const [viewingDecennale, setViewingDecennale] = useState<PartnerDecennale | null>(null);
  const [editingDecennale, setEditingDecennale] = useState<string | null>(null); // start_date of the one being edited
  const [editStartDate, setEditStartDate] = useState("");
  const [editEndDate, setEditEndDate] = useState("");
  const [savingDates, setSavingDates] = useState(false);

  function handleSave() {
    if (!partner.display_name && !partner.company_name) {
      showNotification({
        type: "error",
        message: "Veuillez renseigner un nom ou une entreprise",
      });
      return;
    }
    updatePartner(partner);
    showNotification({
      type: "success",
      message: "Artisan modifié avec succès",
    });
    onClose();
  }

  async function handleDeleteDecennale(startDate: string) {
    try {
      const updated = await partnersApi.deleteDecennale(partner.id, startDate);
      setPartner(updated);
      updatePartner(updated);
      showNotification({ type: "success", message: "Décennale supprimée" });
    } catch {
      showNotification({ type: "error", message: "Erreur lors de la suppression" });
    }
  }

  function startEditingDecennale(d: PartnerDecennale) {
    const key = d.start_date || (d.year ? `${d.year}-01-01` : "");
    setEditingDecennale(key);
    setEditStartDate(d.start_date || (d.year ? `${d.year}-01-01` : ""));
    setEditEndDate(d.end_date || (d.year ? `${d.year}-12-31` : ""));
  }

  async function handleSaveDates() {
    if (!editingDecennale || !editStartDate || !editEndDate) return;
    if (editStartDate >= editEndDate) {
      showNotification({ type: "error", message: "La date de fin doit être postérieure à la date de début" });
      return;
    }
    setSavingDates(true);
    try {
      const updated = await partnersApi.updateDecennaleDates(partner.id, editingDecennale, editStartDate, editEndDate);
      setPartner(updated);
      updatePartner(updated);
      setEditingDecennale(null);
      showNotification({ type: "success", message: "Dates mises à jour" });
    } catch {
      showNotification({ type: "error", message: "Erreur lors de la mise à jour" });
    } finally {
      setSavingDates(false);
    }
  }

  return (
    <>
    <BaseModal
      title="Modifier l'intervenant"
      startIcon={<Icon1 name="intervenant" size={20} />}
      onClose={onClose}
    >
      <div className="bg-white flex flex-col gap-2 p-4">
        <div className="text-h3 text-neutral-400 font-semibold">Entreprise</div>
        <div className="flex flex-row gap-2">
          <div className="flex-1">
            <TextField
              label="Nom entreprise"
              placeholder="Construction ABC"
              value={partner.company_name || ""}
              onChange={(value) =>
                setPartner({ ...partner, company_name: value })
              }
            />
          </div>
          <div className="flex-1">
            <DropDownMultiple
              options={COMPANY_TYPE_OPTIONS}
              label="Type"
              value={(partner.company_types || ["autre"]).filter(
                (t) => !HIDDEN_COMPANY_TYPES.has(t),
              )}
              onSelect={(values) => {
                const preserved = (partner.company_types || []).filter((t) =>
                  HIDDEN_COMPANY_TYPES.has(t),
                );
                setPartner({
                  ...partner,
                  company_types: [...(values as string[]), ...preserved],
                });
              }}
            />
          </div>
        </div>
        <div className="flex flex-row gap-2">
          <TextField
            label="Tel entreprise"
            placeholder="01 23 45 67 89"
            type="phone"
            value={partner.company_phone || ""}
            onChange={(value) =>
              setPartner({ ...partner, company_phone: value })
            }
          />
          <TextField
            label="Email entreprise"
            placeholder="contact@entreprise.fr"
            value={partner.company_email || ""}
            onChange={(value) =>
              setPartner({ ...partner, company_email: value })
            }
          />
        </div>
        <div className="flex flex-row gap-2">
          <TextField
            label="Adresse"
            placeholder="12 Rue de la Paix"
            value={partner.company_address || ""}
            onChange={(value) =>
              setPartner({ ...partner, company_address: value })
            }
          />
          <TextField
            label="Code postal"
            placeholder="75002"
            value={partner.company_postal || ""}
            onChange={(value) =>
              setPartner({ ...partner, company_postal: value })
            }
          />
        </div>
        <TextField
          label="Ville"
          placeholder="Paris"
          value={partner.company_city || ""}
          onChange={(value) =>
            setPartner({ ...partner, company_city: value })
          }
        />

        <div className="border-t border-neutral-200 my-2" />
        <div className="text-h3 text-neutral-400 font-semibold">Contact</div>
        <TextField
          label="Nom complet"
          placeholder="Sophie Martin"
          value={partner.display_name}
          onChange={(value) =>
            setPartner({ ...partner, display_name: value })
          }
        />
        <div className="flex flex-row gap-2">
          <TextField
            label="Téléphone"
            placeholder="06 12 34 56 78"
            type="phone"
            value={partner.phone_number || ""}
            onChange={(value) =>
              setPartner({ ...partner, phone_number: value })
            }
          />
          <TextField
            label="Email"
            placeholder="sophie@martin.fr"
            value={partner.email || ""}
            onChange={(value) => setPartner({ ...partner, email: value })}
          />
        </div>

        <div className="border-t border-neutral-200 my-2" />
        <div className="text-h3 text-neutral-400 font-semibold">Assurances décennales</div>

        {/* Existing décennales */}
        {(partner.decennales || []).length > 0 && (
          <div className="flex flex-col gap-1">
            {(partner.decennales || [])
              .slice()
              .sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""))
              .map((d: PartnerDecennale) => {
                const decKey = d.start_date || (d.year ? `${d.year}-01-01` : "");
                const isEditing = editingDecennale === decKey;
                const label = d.start_date && d.end_date
                  ? `${formatIsoDate(d.start_date)} → ${formatIsoDate(d.end_date)}`
                  : d.year ? `Année ${d.year}` : "Décennale";

                if (isEditing) {
                  return (
                    <div
                      key={d.start_date || d.year}
                      className="flex flex-col gap-2 px-3 py-2 bg-blue-50 rounded-lg border border-blue-200"
                    >
                      <div className="flex flex-row gap-2">
                        <div className="flex flex-col gap-1 flex-1">
                          <label className="text-h5 text-neutral-500">Début</label>
                          <input
                            type="date"
                            value={editStartDate}
                            onChange={(e) => setEditStartDate(e.target.value)}
                            className="border border-neutral-300 rounded-lg px-2 py-1 text-h4"
                          />
                        </div>
                        <div className="flex flex-col gap-1 flex-1">
                          <label className="text-h5 text-neutral-500">Fin</label>
                          <input
                            type="date"
                            value={editEndDate}
                            onChange={(e) => setEditEndDate(e.target.value)}
                            className="border border-neutral-300 rounded-lg px-2 py-1 text-h4"
                          />
                        </div>
                      </div>
                      <div className="flex flex-row gap-2 justify-end">
                        <SecondaryButton variant="small" onClick={() => setEditingDecennale(null)}>
                          Annuler
                        </SecondaryButton>
                        <Button
                          variant="small"
                          label={savingDates ? "..." : "Enregistrer"}
                          onClick={handleSaveDates}
                        />
                      </div>
                    </div>
                  );
                }

                return (
                  <div
                    key={d.start_date || d.year}
                    className="flex flex-row items-center gap-2 px-3 py-2 bg-neutral-50 rounded-lg border border-neutral-200"
                  >
                    <button
                      onClick={() => d.url && setViewingDecennale(d)}
                      className={`text-h3 font-medium flex-1 text-left flex items-center gap-2 ${d.url ? "text-neutral-700 hover:text-black cursor-pointer" : "text-neutral-400"}`}
                    >
                      <Icon1 name="pdf" size={16} />
                      {label}
                    </button>
                    <button
                      onClick={() => startEditingDecennale(d)}
                      className="text-neutral-400 hover:text-neutral-600"
                      title="Modifier les dates"
                    >
                      <Icon1 name="modify" size={18} />
                    </button>
                    {d.url && (
                      <a
                        href={d.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-neutral-400 hover:text-neutral-600"
                      >
                        <Icon1 name="open" size={20} />
                      </a>
                    )}
                    <button
                      onClick={() => handleDeleteDecennale(d.start_date)}
                      className="text-red-400 hover:text-red-600"
                    >
                      <Icon1 name="delete" size={20} />
                    </button>
                  </div>
                );
              })}
          </div>
        )}

        {/* Add new décennale */}
        <SecondaryButton variant="small" onClick={() => setShowAddDecennale(true)}>
          Ajouter une décennale
        </SecondaryButton>
      </div>
      <div className="border-t-1 p-4 flex flex-row justify-between">
        {canDelete ? (
          <SecondaryButton
            variant="small"
            danger={true}
            onClick={() => {
              removePartners([initialPartner.id]);
              showNotification({
                type: "success",
                message: "Artisan supprimé",
              });
              onClose();
            }}
          >
            Supprimer
          </SecondaryButton>
        ) : (
          <div />
        )}
        <div className="flex flex-row gap-2">
          <SecondaryButton variant="small" onClick={onClose}>
            Annuler
          </SecondaryButton>
          <Button
            variant="small"
            label="Enregistrer"
            onClick={handleSave}
          />
        </div>
      </div>
    </BaseModal>
    {viewingDecennale?.url && (
      <PDFViewerModal
        pdfUrl={viewingDecennale.url}
        title={viewingDecennale.start_date ? `Décennale ${formatIsoDate(viewingDecennale.start_date)} → ${formatIsoDate(viewingDecennale.end_date)}` : `Décennale ${viewingDecennale.year}`}
        onClose={() => setViewingDecennale(null)}
      />
    )}
    {showAddDecennale && (
      <AddDecennaleModal
        partnerId={partner.id}
        partnerName={partner.company_name || partner.display_name}
        onClose={() => {
          setShowAddDecennale(false);
          // Refresh partner data from store. `partners` holds project-scoped
          // partners, `partnersList` holds the org-level list used by the
          // Artisans page. Check both so the newly-added décennale is picked
          // up regardless of where this modal was opened from.
          const state = useProjectStore.getState();
          const updated =
            state.partners.find((p) => p.id === partner.id) ||
            state.partnersList.find((p) => p.id === partner.id);
          if (updated) setPartner(updated);
        }}
      />
    )}
    </>
  );
};
